/**
 * Content Buckets Page
 * 
 * Main page for managing content buckets including listing, creating, editing,
 * and deleting buckets. Also provides access to industry templates.
 * 
 * Features:
 * - Display user's content buckets in a responsive grid
 * - Create new buckets manually or from templates
 * - Edit and delete existing buckets
 * - Search and filter functionality
 * - Mobile-responsive design
 * 
 * @author AI Assistant
 * @created 2024
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Fab,
  CircularProgress,
  Fade,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  ViewModule as GridIcon,
  ViewList as ListIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import { useContentBuckets } from '../providers/ContentBucketsProvider';
import ContentBucketCard from '../components/ContentBucketCard';
import ContentBucketForm from '../components/ContentBucketForm';
import IndustryTemplateSelector from '../components/IndustryTemplateSelector';
import ContentBucketList from '../components/ContentBucketList';
import EmptyBucketsState from '../components/EmptyBucketsState';

const PageHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(2)
  }
}));

const ControlsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    '& > *': {
      width: '100%'
    }
  }
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  maxWidth: 400,
  [theme.breakpoints.down('sm')]: {
    maxWidth: 'none'
  }
}));

const ViewToggleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    justifyContent: 'center'
  }
}));

const ContentBucketsPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const {
    buckets,
    loading,
    error,
    fetchBuckets,
    clearError,
    bucketCount,
    totalPosts
  } = useContentBuckets();

  // Local state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTemplate, setFilterTemplate] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);

  // Load buckets on component mount
  useEffect(() => {
    fetchBuckets();
  }, [fetchBuckets]);

  // Apply filters
  const filteredBuckets = buckets.filter(bucket => {
    const matchesSearch = !searchTerm || 
      bucket.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bucket.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesTemplate = !filterTemplate || bucket.industry_template === filterTemplate;
    
    const matchesActive = filterActive === 'all' || 
      (filterActive === 'active' && bucket.is_active) ||
      (filterActive === 'inactive' && !bucket.is_active);

    return matchesSearch && matchesTemplate && matchesActive;
  });

  // Handle search
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Handle create new bucket
  const handleCreateNew = () => {
    setShowCreateForm(true);
  };

  // Handle create from template
  const handleCreateFromTemplate = () => {
    setShowTemplateSelector(true);
  };

  // Handle close forms
  const handleCloseCreateForm = () => {
    setShowCreateForm(false);
  };

  const handleCloseTemplateSelector = () => {
    setShowTemplateSelector(false);
  };

  // Handle form success
  const handleCreateSuccess = () => {
    setShowCreateForm(false);
    setShowTemplateSelector(false);
  };

  // Get unique templates for filter
  const availableTemplates = [...new Set(buckets.map(bucket => bucket.industry_template))]
    .filter(Boolean);

  if (loading && buckets.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={48} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Page Header */}
      <PageHeader>
        <Typography 
          variant="h3" 
          component="h1" 
          gutterBottom
          sx={{
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            fontWeight: 700
          }}
        >
          Content Buckets
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
        >
          Organize your content into thematic categories for better planning and variety.
          {bucketCount > 0 && (
            <> You have {bucketCount} bucket{bucketCount !== 1 ? 's' : ''} with {totalPosts} total posts.</>
          )}
        </Typography>
      </PageHeader>

      {/* Error Alert */}
      {error && (
        <Fade in={!!error}>
          <Alert 
            severity="error" 
            onClose={clearError}
            sx={{ mb: 3 }}
          >
            {error}
          </Alert>
        </Fade>
      )}

      {/* Empty State */}
      {buckets.length === 0 && !loading && (
        <EmptyBucketsState 
          onCreateNew={handleCreateNew}
          onCreateFromTemplate={handleCreateFromTemplate}
        />
      )}

      {/* Controls and Content */}
      {buckets.length > 0 && (
        <>
          {/* Search and Filter Controls */}
          <ControlsContainer>
            {/* Search */}
            <SearchContainer>
              <TextField
                fullWidth
                placeholder="Search buckets..."
                value={searchTerm}
                onChange={handleSearchChange}
                size={isMobile ? "medium" : "small"}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </SearchContainer>

            {/* Template Filter */}
            <FormControl size={isMobile ? "medium" : "small"} sx={{ minWidth: 140 }}>
              <InputLabel>Template</InputLabel>
              <Select
                value={filterTemplate}
                onChange={(e) => setFilterTemplate(e.target.value)}
                label="Template"
              >
                <MenuItem value="">All Templates</MenuItem>
                {availableTemplates.map(template => (
                  <MenuItem key={template} value={template}>
                    {template.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Active Filter */}
            <FormControl size={isMobile ? "medium" : "small"} sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterActive}
                onChange={(e) => setFilterActive(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>

            {/* View Toggle */}
            {!isMobile && (
              <ViewToggleContainer>
                <Button
                  variant={viewMode === 'grid' ? 'contained' : 'outlined'}
                  onClick={() => setViewMode('grid')}
                  startIcon={<GridIcon />}
                  size="small"
                >
                  Grid
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'contained' : 'outlined'}
                  onClick={() => setViewMode('list')}
                  startIcon={<ListIcon />}
                  size="small"
                >
                  List
                </Button>
              </ViewToggleContainer>
            )}

            {/* Create Buttons */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                onClick={handleCreateFromTemplate}
                size={isMobile ? "medium" : "small"}
                startIcon={<FilterIcon />}
              >
                From Template
              </Button>
              <Button
                variant="contained"
                onClick={handleCreateNew}
                size={isMobile ? "medium" : "small"}
                startIcon={<AddIcon />}
              >
                New Bucket
              </Button>
            </Box>
          </ControlsContainer>

          {/* Buckets Display */}
          {filteredBuckets.length === 0 ? (
            <Box 
              display="flex" 
              flexDirection="column" 
              alignItems="center" 
              justifyContent="center"
              minHeight="300px"
              textAlign="center"
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No buckets match your filters
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your search or filter criteria
              </Typography>
            </Box>
          ) : (
            <>
              {viewMode === 'grid' ? (
                <Grid container spacing={3}>
                  {filteredBuckets.map(bucket => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={bucket.id}>
                      <ContentBucketCard bucket={bucket} />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <ContentBucketList buckets={filteredBuckets} />
              )}
            </>
          )}
        </>
      )}

      {/* Floating Action Button for Mobile */}
      {isMobile && buckets.length > 0 && (
        <Fab
          color="primary"
          aria-label="add bucket"
          onClick={handleCreateNew}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
          }}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Create Form Dialog */}
      <ContentBucketForm
        open={showCreateForm}
        onClose={handleCloseCreateForm}
        onSuccess={handleCreateSuccess}
      />

      {/* Template Selector Dialog */}
      <IndustryTemplateSelector
        open={showTemplateSelector}
        onClose={handleCloseTemplateSelector}
        onSuccess={handleCreateSuccess}
      />
    </Container>
  );
};

export default ContentBucketsPage; 